/**
 * Who works on a client, and what the client gets every month.
 *
 * Both are partner decisions and both are read by everybody on the client, so
 * each card shows the same thing to everyone and grows controls only for a
 * partner. The server refuses the writes for anybody else regardless.
 */

import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AssignmentRole, PlanLine } from '../../api';
import { Avatar } from '../../components/Avatar';
import { BentoCard } from '../../components/BentoCard';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import { useAppData } from '../../context/AppDataContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import type { Client } from '../../data/types';
import { apiErrorMessage } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useApi } from '../../useApi';
import { progressOf, type Project } from '../projects/types';
import { iconFor } from './DeliverablesBoard';

export const ROLE_LABEL: Record<AssignmentRole, string> = {
  lead: 'Main contact',
  member: 'Working on it',
  view: 'Can view'
};
export const ROLES: AssignmentRole[] = ['lead', 'member', 'view'];

/** A compact picker. Not `field-input`, whose `width: 100%` would override any width given here. */
export const PICK =
  'h-9 rounded-lg border border-shell-border bg-shell px-2.5 text-sm text-shell-text-active hover:border-shell-text focus:border-brand-400';

/** A plan row being edited. Its own object, so editing never touches what is saved. */
interface DraftLine {
  key: number;
  metric: string;
  count: string;
  unit: string;
}

/** The three kinds the editor names; any other unit ("branches") is kept as it is. */
const KINDS: Array<{ value: string; label: string }> = [
  { value: 'pieces', label: 'Counted' },
  { value: 'active', label: 'Ongoing service' },
  { value: 'project', label: 'One-time job' }
];

function describe(line: PlanLine): { text: string; tag: string | null } {
  if (line.unit === 'active') {
    return {
      text: line.target_count > 1 ? `${line.target_count} × ${line.metric}` : line.metric,
      tag: 'ongoing'
    };
  }
  if (line.unit === 'project') return { text: line.metric, tag: 'one-time' };
  if (line.unit === 'pieces') return { text: `${line.target_count} ${line.metric}`, tag: null };
  // A noun of its own: "Google Ads Management · 2 branches".
  return { text: `${line.metric} · ${line.target_count} ${line.unit}`, tag: null };
}

export function ClientPeopleAndPlan({ client }: { client: Client }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <PeopleCard client={client} />
      <PlanCard client={client} />
    </div>
  );
}

function PeopleCard({ client }: { client: Client }) {
  const { assignments, assignPerson, unassignPerson, team, directory } = useAppData();
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  // Staffing a client and setting its plan is a manager's job as much as an Admin's.
  const isPartner = hasRole('PARTNER', 'MANAGER');
  const [adding, setAdding] = useState('');
  const [addRole, setAddRole] = useState<AssignmentRole>('member');
  const [busy, setBusy] = useState(false);

  const onClient = assignments
    .filter((a) => a.client_id === client.id)
    // Main contacts first, then the people doing the work, then watchers.
    .sort((a, b) => ROLES.indexOf(a.role) - ROLES.indexOf(b.role));
  const available = team.filter((m) => !onClient.some((a) => a.person_id === m.id));

  const run = async (work: () => Promise<void>, said: string) => {
    setBusy(true);
    try {
      await work();
      showToast(said);
    } catch (err) {
      showToast(apiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <BentoCard title="Team">
      {onClient.length === 0 ? (
        <p className="text-sm text-shell-text">
          Nobody yet. Somebody on the team sees this client only once they are on it.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {onClient.map((a) => {
            const person = directory.find((d) => d.id === a.person_id);
            const name = person?.name ?? a.person_id;
            return (
              // Wraps rather than squeezes: in a narrow card the role picker
              // drops below the name instead of pushing the name to nothing.
              <li key={a.person_id} className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <Avatar name={name} color={person?.color} size="md" />
                <div className="min-w-[7rem] flex-1">
                  <Link
                    to={`/people/${encodeURIComponent(a.person_id)}`}
                    className="block truncate text-sm font-medium text-shell-text-active hover:underline"
                  >
                    {name}
                  </Link>
                  {person !== undefined && <p className="truncate text-xs text-shell-text">{person.title}</p>}
                </div>
                {isPartner ? (
                  <>
                    <Select
                      aria-label={`How ${name} is on ${client.name}`}
                      value={a.role}
                      disabled={busy}
                      onChange={(e) => {
                        const role = e.target.value as AssignmentRole;
                        void run(
                          () => assignPerson(client.id, a.person_id, role),
                          `${name} is now ${ROLE_LABEL[role].toLowerCase()} on ${client.name}.`
                        );
                      }}
                      className={cn(PICK, "w-36 shrink-0")}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Take ${name} off ${client.name}`}
                      title={`Take ${name} off ${client.name}`}
                      onClick={() =>
                        void run(
                          () => unassignPerson(client.id, a.person_id),
                          `${name} is off ${client.name}.`
                        )
                      }
                      className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-shell-text hover:bg-shell-border/40 hover:text-danger-500"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <span className="shrink-0 rounded-full bg-shell-border/40 px-2.5 py-1 text-xs text-shell-text">
                    {ROLE_LABEL[a.role]}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {isPartner && available.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-shell-border pt-4">
          <Select
            aria-label="Person to add"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            className={cn(PICK, "min-w-[10rem] flex-1")}
          >
            <option value="">Add somebody…</option>
            {available.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="As"
            value={addRole}
            onChange={(e) => setAddRole(e.target.value as AssignmentRole)}
            className={cn(PICK, "w-36")}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
          <Button
            size="sm"
            disabled={adding === '' || busy}
            onClick={() => {
              const person = available.find((m) => m.id === adding);
              void run(async () => {
                await assignPerson(client.id, adding, addRole);
                setAdding('');
              }, `${person?.name ?? 'They'} ${person === undefined ? 'are' : 'is'} on ${client.name}.`);
            }}
          >
            Add
          </Button>
        </div>
      )}
    </BentoCard>
  );
}

function PlanCard({ client }: { client: Client }) {
  const { loadPlan, savePlan } = useAppData();
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  // Staffing a client and setting its plan is a manager's job as much as an Admin's.
  const isPartner = hasRole('PARTNER', 'MANAGER');

  // A one-time job on the plan is usually a project. Where one is linked, the
  // row shows how far along it is instead of a line nothing can ever tick.
  const { result: projectResult } = useApi<Project[]>('/projects', { query: { client: client.id } });
  const projectFor = (line: PlanLine) =>
    (projectResult.data ?? []).find((p) => p.deliverable_id === line.id);

  const [plan, setPlan] = useState<PlanLine[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftLine[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [nextKey, setNextKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setDraft(null);
    loadPlan(client.id)
      .then((lines) => {
        if (!cancelled) setPlan(lines);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(apiErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [client.id, loadPlan]);

  const startEditing = () => {
    const lines = (plan ?? []).map((l, i) => ({
      key: i,
      metric: l.metric,
      count: String(l.target_count),
      unit: l.unit
    }));
    setNextKey(lines.length);
    setDraft(lines);
  };

  const change = (key: number, patch: Partial<DraftLine>) =>
    setDraft((d) => d?.map((l) => (l.key === key ? { ...l, ...patch } : l)) ?? null);

  const move = (index: number, by: number) =>
    setDraft((d) => {
      if (d === null) return d;
      const to = index + by;
      if (to < 0 || to >= d.length) return d;
      const next = [...d];
      const [line] = next.splice(index, 1);
      if (line !== undefined) next.splice(to, 0, line);
      return next;
    });

  // What is wrong with the draft, in words, or null when it can be saved.
  const problem = (() => {
    if (draft === null) return null;
    const names = new Set<string>();
    for (const l of draft) {
      const name = l.metric.trim();
      if (name === '') return 'Every line needs a name.';
      const n = Number(l.count);
      if (!Number.isInteger(n) || n < 1 || n > 1000) return `"${name}" needs a whole number from 1 up.`;
      if (names.has(name.toLowerCase())) return `"${name}" is in the list twice.`;
      names.add(name.toLowerCase());
    }
    return null;
  })();

  const save = async () => {
    if (draft === null || problem !== null) return;
    setSaving(true);
    try {
      const saved = await savePlan(
        client.id,
        draft.map((l) => ({ metric: l.metric.trim(), targetCount: Number(l.count), unit: l.unit }))
      );
      setPlan(saved);
      setDraft(null);
      showToast('Plan saved. This month now matches it.');
    } catch (err) {
      showToast(apiErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BentoCard
      title="Monthly plan"
      action={
        isPartner && draft === null && plan !== null ? (
          <Button size="sm" variant="secondary" onClick={startEditing}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {draft !== null ? (
        <div className="flex flex-col gap-2">
          {draft.map((l, i) => {
            // A unit the editor has no name for stays selectable as itself.
            const kinds = KINDS.some((k) => k.value === l.unit) ? KINDS : [...KINDS, { value: l.unit, label: `Counted in ${l.unit}` }];
            return (
              <div
                key={l.key}
                className="grid grid-cols-[auto_3.5rem_minmax(0,1fr)_auto] items-center gap-2 rounded-xl bg-shell-border/30 p-2"
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="text-shell-text hover:text-shell-text-active disabled:opacity-30"
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={i === draft.length - 1}
                    onClick={() => move(i, 1)}
                    className="text-shell-text hover:text-shell-text-active disabled:opacity-30"
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
                <input
                  aria-label="How many"
                  inputMode="numeric"
                  value={l.count}
                  onChange={(e) => change(l.key, { count: e.target.value.replace(/[^0-9]/g, '') })}
                  className="field-input !px-2 !py-1.5 text-center tabular-nums"
                />
                <input
                  aria-label="What"
                  value={l.metric}
                  placeholder="Reels, Articles, Google Ads Management"
                  onChange={(e) => change(l.key, { metric: e.target.value })}
                  className="field-input !py-1.5"
                />
                <button
                  type="button"
                  aria-label="Remove this line"
                  onClick={() => setDraft((d) => d?.filter((x) => x.key !== l.key) ?? null)}
                  className="grid h-7 w-7 place-items-center rounded-md text-shell-text hover:bg-shell-border/40 hover:text-danger-500"
                >
                  <X size={14} />
                </button>
                <Select
                  aria-label="Kind"
                  value={l.unit}
                  onChange={(e) => change(l.key, { unit: e.target.value })}
                  className={cn(PICK, "col-span-3 col-start-2 w-full")}
                >
                  {kinds.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </div>
            );
          })}
          {problem !== null && <p className="text-xs text-danger-500">{problem}</p>}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setDraft((d) => [...(d ?? []), { key: nextKey, metric: '', count: '1', unit: 'pieces' }]);
                setNextKey((k) => k + 1);
              }}
            >
              <Plus size={14} /> Add a line
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" disabled={saving} onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button size="sm" disabled={saving || problem !== null} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : plan === null ? (
        <p className="text-sm text-shell-text">{loadError ?? 'Loading…'}</p>
      ) : plan.length === 0 ? (
        <p className="text-sm text-shell-text">
          Nothing set yet. Say what this client gets every month — 4 Reels, 2 Articles, Google Ads Management.
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {plan.map((line) => {
            const Icon = iconFor(line.metric);
            const { text, tag } = describe(line);
            const project = projectFor(line);
            return (
              <li key={line.id} className="flex items-center gap-3">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-500">
                  <Icon size={15} />
                </span>
                <span className="min-w-0 flex-1 text-sm font-medium text-shell-text-active">{text}</span>
                {project !== undefined ? (
                  <Link
                    to={`/projects/${project.id}`}
                    className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:underline"
                  >
                    {project.tasks === 0
                      ? 'Project started'
                      : `${progressOf(project) ?? 0}% · ${project.done} of ${project.tasks}`}
                  </Link>
                ) : (
                  tag !== null && (
                    <span className="shrink-0 rounded-full bg-shell-border/40 px-2 py-0.5 text-[11px] text-shell-text">
                      {tag}
                    </span>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
    </BentoCard>
  );
}
